<template>
  <Main>
    <ToolsPanel
      :breadCrumbFirst="'Categories Groups'"
      :breadCrumbFirstLink="getFirstLink"
      :breadCrumbSecond="'Categories Group Edit'"
      :title="'Categories Group Edit'"
    >
      <div class="form-create__buttons">
        <MainButton buttonWhite @click.native="handleSubmit">Update</MainButton>
        <MainButton @click.native="handleBackToList">Back</MainButton>
        <div v-if="langList.length" class="form-create__langs">
          <div
            v-for="lang in langList"
            :key="lang.id"
            @click="openLangModal(lang)"
            class="form-create__lang"
          >
            {{ lang.seoName }}
          </div>
        </div>
      </div>
    </ToolsPanel>
    <ContentContainer>
      <div class="form-create">
        <form @submit.prevent="handleSubmit">
          <p class="form-create__label">Name</p>
          <div class="input-container">
            <InputText
              :onChange="setName"
              :id="'name'"
              :label="'name'"
              :type="'text'"
              :value="formDataToSend.name"
              :placeholder="'Name'"
              :error-messages="validationErrors['name']"
              isBordered
            />
          </div>
          <p class="form-create__label">SEO Name</p>
          <div class="input-container">
            <InputText
              :onChange="setName"
              :id="'seoName'"
              :label="'seoName'"
              :type="'text'"
              :value="formDataToSend.seo_name"
              :placeholder="'SEO name'"
              :error-messages="validationErrors['seoName']"
              readonly
              isBordered
            />
          </div>

          <div class="input-container">
            <MainCheckbox :label="'Active'" :id="'status'" :set-value="formDataToSend" />
          </div>

          <p class="form-create__label">H1</p>
          <div class="input-container">
            <InputText
              :set-value="formDataToSend"
              :id="'h1'"
              :label="'h1'"
              :type="'text'"
              :placeholder="'H1'"
              :error-messages="validationErrors['h1']"
              isBordered
            />
          </div>

          <p class="form-create__label">Title</p>
          <div class="input-container">
            <InputText
              :set-value="formDataToSend"
              :id="'title'"
              :label="'title'"
              :type="'text'"
              :placeholder="'Title'"
              :error-messages="validationErrors['title']"
              isBordered
            />
          </div>

          <p class="form-create__label">Description</p>
          <div class="input-container">
            <InputText
              :set-value="formDataToSend"
              :id="'description'"
              :label="'description'"
              :type="'text'"
              :placeholder="'Description'"
              :max-length="'255'"
              :error-messages="validationErrors['description']"
              isBordered
            />
          </div>

          <p class="form-create__label">Annotation</p>
          <div class="input-container">
            <InputTextarea
              :set-value="formDataToSend"
              :id="'annotation'"
              :label="'annotation'"
              :placeholder="''"
              :error-messages="validationErrors['annotation']"
              isBordered
            />
          </div>

          <p class="form-create__label">SEO Text</p>
          <div class="input-container">
            <TextEditor
              :set-value="formDataToSend"
              :id="'seo_text'"
              :placeholder="''"
              :error-messages="validationErrors['seo_text']"
            />
          </div>

          <p class="form-create__label">Search terms</p>
          <div class="form-create__terms">
            <div
              v-for="(item, index) in formDataToSend.search_terms"
              :key="index"
              class="form-create__term"
            >
              <input class="input-terms" type="text" v-model="formDataToSend.search_terms[index]" />
              <div @click="handleDeleteRowTerm(index)" class="form-create__terms-delete">
                <CancelIcon />
              </div>
            </div>
          </div>
          <div class="form-create__add-size">
            <MainButton @click.native="handleAddRowTerm">Add search terms</MainButton>
          </div>

          <div class="form-create__radio-group">
            <div class="form-create__radio-group-item">
              <p class="form-create__label">Group Parent</p>
              <div class="input-container checkbox-container">
                <InputRadio
                  :set-value="formDataToSend"
                  :id="'parent'"
                  :values="categoriesGroupsTree"
                  :name="'name'"
                  :value="'id'"
                />
              </div>
            </div>
          </div>

          <p class="form-create__label">Attributes</p>
          <div class="form-create__chosen-items">
            <div
              v-for="(item, index) in formDataToSend.attributes"
              :key="index"
              class="form-create__chosen-item"
            >
              {{ item.name }} (seo: {{ item.seoName }})
              <span v-if="!item.parentId"><b>is parent</b></span>

              <div @click="deleteAttribute(index)" class="form-create__check-item-add">-</div>
            </div>
          </div>

          <input
            v-model="attributeSearch"
            class="form-create__search"
            type="text"
            placeholder="Search attribute..."
            @input="debouncedGetAttributes"
          />

          <div class="input-container checkbox-container checkbox-container_margin">
            <div v-for="(item, index) in attributesTree" :key="index">
              <div class="form-create__check-item">
                <b>{{ item.name }} (seo: {{ item.seoName }})</b>
                <div @click="addAttribute(item)" class="form-create__check-item-add">+</div>
              </div>
              <div v-if="item.children.length">
                <div
                  v-for="(el, i) in item.children[0]"
                  :key="i"
                  class="form-create__check-item child"
                >
                  {{ el.name }} (seo: {{ el.seoName }})
                  <div @click="addAttribute(el)" class="form-create__check-item-add">+</div>
                </div>
              </div>
            </div>
          </div>

          <p class="form-create__label">Categories</p>
          <div class="form-create__chosen-items">
            <div
              v-for="(item, index) in formDataToSend.categories"
              :key="index"
              class="form-create__chosen-item"
            >
              {{ item.fullName || item.name }}

              <div @click="deleteCategory(index)" class="form-create__check-item-add">-</div>
            </div>
          </div>

          <input
            v-model="categorySearch"
            class="form-create__search"
            type="text"
            placeholder="Search category..."
            @input="debouncedGetCategories"
          />

          <div class="input-container checkbox-container checkbox-container_margin">
            <div
              v-for="(item, index) in categoriesList"
              :key="index"
              class="form-create__check-item"
            >
              {{ item.fullName }}
              <div @click="addCategory(item)" class="form-create__check-item-add">+</div>
            </div>
          </div>

          <div v-if="error.message != undefined" class="form-create__error">
            {{ error.message }}
          </div>
          <div class="form-create__btn">
            <MainButton :type="'submit'">Update</MainButton>
          </div>
        </form>
        <MainModal v-if="langModal" :on-close="closeLangModal" :modal-center="true">
          <div class="lang-modal">
            <div class="lang-modal__title">Translations for {{ chosenLang.name }}</div>
            <form @submit.prevent="handleSubmitLang">
              <p class="form-create__label">Name</p>
              <div class="input-container">
                <InputText
                  :set-value="formDataToSendLang"
                  :id="'name'"
                  :label="'name'"
                  :type="'text'"
                  :placeholder="'name'"
                  isBordered
                />
                <div
                  @click="handleTranslate('name', chosenLang.name)"
                  class="form-create__translate"
                >
                  Translate (Chat GPT)
                </div>
              </div>
              <p class="form-create__label">H1</p>
              <div class="input-container">
                <InputText
                  :set-value="formDataToSendLang"
                  :id="'h1'"
                  :label="'h1'"
                  :type="'text'"
                  :placeholder="'H1'"
                  isBordered
                />
                <div @click="handleTranslate('h1', chosenLang.name)" class="form-create__translate">
                  Translate (Chat GPT)
                </div>
              </div>

              <p class="form-create__label">Title</p>
              <div class="input-container">
                <InputText
                  :set-value="formDataToSendLang"
                  :id="'title'"
                  :label="'title'"
                  :type="'text'"
                  :placeholder="'Title'"
                  isBordered
                />
                <div
                  @click="handleTranslate('title', chosenLang.name)"
                  class="form-create__translate"
                >
                  Translate (Chat GPT)
                </div>
              </div>

              <p class="form-create__label">Description</p>
              <div class="input-container">
                <InputText
                  :set-value="formDataToSendLang"
                  :id="'description'"
                  :label="'description'"
                  :type="'text'"
                  :max-length="'255'"
                  :placeholder="'Description'"
                  isBordered
                />
                <div
                  @click="handleTranslate('description', chosenLang.name)"
                  class="form-create__translate"
                >
                  Translate (Chat GPT)
                </div>
              </div>

              <p class="form-create__label">Annotation</p>
              <div class="input-container">
                <InputTextarea
                  :set-value="formDataToSendLang"
                  :id="'annotation'"
                  :label="'annotation'"
                  :placeholder="''"
                  isBordered
                />
                <div
                  @click="handleTranslate('annotation', chosenLang.name)"
                  class="form-create__translate"
                >
                  Translate (Chat GPT)
                </div>
              </div>

              <p class="form-create__label">SEO Text</p>
              <div class="input-container">
                <TextEditor :set-value="formDataToSendLang" :id="'seo_text'" :placeholder="''" />
                <div
                  @click="handleTranslate('seo_text', chosenLang.name)"
                  class="form-create__translate"
                >
                  Translate (Chat GPT)
                </div>
              </div>
              <div class="form-create__btn">
                <MainButton :type="'submit'">Update</MainButton>
              </div>
            </form>
          </div>
        </MainModal>
        <ModalError
          :error-modal="false"
          :handle-close-error-modal="
            () => {
              this.error = {};
            }
          "
        />
      </div>
    </ContentContainer>
    <FetchSpinnerModal v-if="loadingProcess" />
  </Main>
</template>

<script>
import { mapState } from "vuex";
import ROUTE from "~/constants/routes";
import categoriesApi from "~/api/categories";
import { debounce } from "lodash";
import langApi from "~/api/lang";
import referencesApi from "~/api/references";

import Main from "~/templates/Main.vue";
import FetchSpinnerModal from "~/components/molecules/FetchSpinnerModal.vue";
import ToolsPanel from "~/components/molecules/ToolsPanel.vue";
import ContentContainer from "~/components/molecules/ContentContainer.vue";
import MainButton from "~/components/atoms/buttons/MainButton.vue";
import InputText from "~/components/atoms/inputs/InputText.vue";
import ModalError from "~/components/molecules/ModalError.vue";
import MainCheckbox from "~/components/atoms/inputs/MainCheckbox.vue";
import TextEditor from "~/components/atoms/inputs/TextEditor.vue";
import InputTextarea from "~/components/atoms/inputs/InputTextarea.vue";
import InputRadio from "~/components/atoms/inputs/InputRadio.vue";
import MainModal from "~/components/molecules/MainModal.vue";
import CancelIcon from "~/assets/images/icons/cancel-icon.svg";

export default {
  name: "CategoriesGroupEdit",
  metaInfo: {
    title: "Categories Group Edit",
  },
  data() {
    return {
      formDataToSend: {
        name: "",
        seo_name: "",
        status: false,
        h1: "",
        title: "",
        description: "",
        annotation: "",
        seo_text: "",
        parent: null,
        categories: [],
        search_terms: [""],
        attributes: [],
      },
      categorySearch: "",
      categoriesList: [],
      attributeSearch: "",
      attributesTree: [],
      attributesAll: [],
      formDataToSendLang: {
        name: "",
        title: "",
        description: "",
        h1: "",
        annotation: "",
        seo_text: "",
        id: null,
      },
      langEdit: false,
      langModal: false,
      chosenLang: null,
    };
  },
  components: {
    Main,
    FetchSpinnerModal,
    ToolsPanel,
    ContentContainer,
    MainButton,
    InputText,
    ModalError,
    MainCheckbox,
    TextEditor,
    InputTextarea,
    InputRadio,
    MainModal,
    CancelIcon,
  },
  computed: {
    ...mapState("categories", [
      "error",
      "categoriesGroup",
      "categoriesGroupsTree",
      "loadingProcess",
      "validationErrors",
    ]),
    ...mapState("lang", ["lang", "langList"]),
    getFirstLink() {
      return ROUTE.CATEGORIES_GROUPS_LIST.replace(":page", 1);
    },
    getId() {
      return this.$route.params.id;
    },
  },
  mounted() {
    this.getAttributesAll();
    this.getCategoriesGroupsTree();
    this.getCategories();
    this.getLang();
    this.getAttributes();
  },
  methods: {
    handleSubmit() {
      const data = {
        name: this.formDataToSend.name != undefined ? this.formDataToSend.name.trim() : "",
        seo_name:
          this.formDataToSend.seo_name != undefined ? this.formDataToSend.seo_name.trim() : "",
        status: this.formDataToSend.status ? 1 : 0,
        h1: this.formDataToSend.h1 != undefined ? this.formDataToSend.h1.trim() : "",
        title: this.formDataToSend.title != undefined ? this.formDataToSend.title.trim() : "",
        description:
          this.formDataToSend.description != undefined
            ? this.formDataToSend.description.trim()
            : "",
        annotation:
          this.formDataToSend.annotation != undefined ? this.formDataToSend.annotation.trim() : "",
        seo_text:
          this.formDataToSend.seo_text != undefined ? this.formDataToSend.seo_text.trim() : "",
      };

      if (this.formDataToSend.parent) {
        data.parent_id = this.formDataToSend.parent;
      }

      data.categories = [];
      if (this.formDataToSend.categories.length) {
        this.formDataToSend.categories.forEach((item, index) => {
          data.categories.push(item.id);
        });
      }

      data.attributes = [];
      if (this.formDataToSend.attributes.length) {
        this.formDataToSend.attributes.forEach((item, index) => {
          data.attributes.push(item.id);
        });
      }

      const sendSearchTerms = [];
      if (this.formDataToSend.search_terms.length > 0) {
        this.formDataToSend.search_terms.forEach(function (item, index) {
          if (item.length > 0) {
            sendSearchTerms.push(item);
          }
        });
      }
      data.search_term = sendSearchTerms;

      const url = `/${this.getId}`;
      this.$store.dispatch("categories/editCategoriesGroup", { url: url, editData: data });
    },
    handleDeleteRowTerm(index) {
      this.formDataToSend.search_terms.splice(index, 1);
    },
    handleAddRowTerm() {
      this.formDataToSend.search_terms.push("");
    },
    getCategoriesGroupsTree() {
      this.$store.dispatch("categories/getCategoriesGroupsTree");
    },
    handleBackToList() {
      this.$router.push({
        path: ROUTE.CATEGORIES_GROUPS_LIST.replace(":page", 1),
      });
    },
    setName(value) {
      this.formDataToSend.name = value.trim();
    },
    getItemCategoriesGroup() {
      const url = `/${this.getId}`;
      try {
        categoriesApi.getItemCategoriesGroup(url).then((res) => {
          this.$store.commit("categories/setError", {});
          this.$store.commit("categories/setValidationErrors", {});
          const data = res.data;
          this.categoryData = res.data;
          this.formDataToSend.name = data.name;
          this.formDataToSend.seo_name = data.seoName;
          this.formDataToSend.status = data.status ? true : false;

          this.formDataToSend.h1 = data.h1;
          this.formDataToSend.title = data.title;
          this.formDataToSend.description = data.description;
          this.formDataToSend.annotation = data.annotation;
          this.formDataToSend.seo_text = data.seoText;
          this.formDataToSend.parent = data.parentId;
          this.formDataToSend.categories = data.categories || [];
          this.formDataToSend.search_terms = data.searchTerm || [];

          if (data.groupAttributes.length) {
            const attributes = [];

            data.groupAttributes.forEach((el) => {
              const item = this.attributesAll.find((u) => u.id == el.attributeId);
              if (item) {
                attributes.push(item);
              }
            });

            this.formDataToSend.attributes = attributes;
          }
        });
      } catch (error) {
        console.log(error);
      }
    },
    getCategories() {
      if (this.categorySearch.trim().length > 0) {
        const data = {
          search: this.categorySearch.trim(),
        };
        try {
          categoriesApi.getCategoriesListFilter("?status=1", data).then((res) => {
            const data = res.data.items.data;
            this.categoriesList = data;
          });
        } catch (error) {
          console.log(error);
        }
      } else {
        try {
          categoriesApi.getCategoriesActive().then((res) => {
            const data = res.data.items;
            this.categoriesList = data;
          });
        } catch (error) {
          console.log(error);
        }
      }
    },
    debouncedGetCategories: debounce(function () {
      this.getCategories();
    }, 300),
    addCategory(item) {
      this.formDataToSend.categories.push(item);
    },
    deleteCategory(index) {
      this.formDataToSend.categories.splice(index, 1);
    },
    getLang() {
      let url = `?page=${this.page}`;
      this.$store.dispatch("lang/getLangList", url);
    },
    closeLangModal() {
      this.chosenLang = null;
      this.langModal = false;
      this.formDataToSendLang.name = "";
      this.formDataToSendLang.h1 = "";
      this.formDataToSendLang.title = "";
      this.formDataToSendLang.description = "";
      this.formDataToSendLang.annotation = "";
      this.formDataToSendLang.seo_text = "";

      this.langEdit = false;
    },
    openLangModal(lang) {
      this.langModal = true;
      this.chosenLang = lang;

      const translate = this.categoryData.translation;
      let translateCategory = translate.find((category) => category.langId === this.chosenLang.id);
      console.log(translateCategory);
      if (translateCategory) {
        this.formDataToSendLang.name = translateCategory.name;
        this.formDataToSendLang.h1 = translateCategory.h1;
        this.formDataToSendLang.title = translateCategory.title;
        this.formDataToSendLang.description = translateCategory.description;
        this.formDataToSendLang.seo_text = translateCategory.seoText;
        this.formDataToSendLang.annotation = translateCategory.annotation;

        this.formDataToSendLang.id = translateCategory.id;
        this.langEdit = true;
      }
    },
    handleSubmitLang() {
      this.$store.commit("references/setLoading", true);
      const data = {
        name: this.formDataToSendLang.name,
        h1: this.formDataToSendLang.h1,
        title: this.formDataToSendLang.title,
        description: this.formDataToSendLang.description,
        annotation: this.formDataToSendLang.annotation,
        seo_text: this.formDataToSendLang.seo_text,
        category_group_id: this.categoryData.id,
        lang_id: this.chosenLang.id,
      };
      if (this.langEdit) {
        langApi
          .editLangCategoryGroups(data, `/${this.formDataToSendLang.id}`)
          .then(() => {
            this.$store.commit("references/setLoading", false);
            this.closeLangModal();
            this.getItemCategoriesGroup();
          })
          .catch((error) => {
            this.$store.commit("references/setLoading", false);
            console.log(error);
          });
      } else {
        langApi
          .createLangCategoryGroups(data)
          .then(() => {
            this.$store.commit("references/setLoading", false);
            this.closeLangModal();
            this.getItemCategoriesGroup();
          })
          .catch((error) => {
            this.$store.commit("references/setLoading", false);
            console.log(error);
          });
      }
    },
    handleTranslate(el, lang) {
      this.$store.commit("references/setLoading", true);
      const data = {
        text: this.formDataToSend[el],
        language: lang,
      };
      langApi
        .getTranslation(data)
        .then((res) => {
          this.$store.commit("references/setLoading", false);
          this.formDataToSendLang[el] = res.data.response;
        })
        .catch((error) => {
          this.$store.commit("references/setLoading", false);
          console.log(error);
        });
    },
    getAttributes() {
      let url = "";
      const search = this.attributeSearch.trim();
      if (search.length) {
        url = `?name=${search}`;
      }
      try {
        referencesApi.getTemplatesAttributesTree(url).then((res) => {
          this.attributesTree = res.data.items;
        });
      } catch (error) {
        console.log(error);
      }
    },
    getAttributesAll() {
      try {
        referencesApi.getTemplatesAttributesAll().then((res) => {
          this.attributesAll = res.data.items;
          this.getItemCategoriesGroup();
        });
      } catch (error) {
        console.log(error);
      }
    },
    debouncedGetAttributes: debounce(function () {
      this.getAttributes();
    }, 300),
    addAttribute(item) {
      const exists = this.formDataToSend.attributes.some((a) => a.id === item.id);
      if (!exists) {
        this.formDataToSend.attributes.push(item);
      }
    },
    deleteAttribute(index) {
      this.formDataToSend.attributes.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.form-create {
  &__terms {
    display: flex;
    flex-wrap: wrap;
  }

  &__term {
    width: 300px;
    position: relative;
    margin-right: 40px;
    margin-bottom: 10px;
  }

  &__terms-delete {
    position: absolute;
    top: 5px;
    right: -30px;
    cursor: pointer;
  }

  &__search {
    font-size: 18px;
    margin-top: 20px;
    width: 400px;
    padding: 5px 10px;
  }

  &__check-item {
    padding: 10px;
    display: flex;
    align-items: center;

    &.child {
      padding-left: 60px;
    }
  }

  &__check-item-add {
    margin-left: 20px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 5px;
    background: #3a80f9;
    border-radius: 50px;
    color: #fff;
    font-size: 18px;
    font-weight: 700;
  }

  &__chosen-items {
    padding-top: 20px;
    display: flex;
    flex-wrap: wrap;
  }

  &__chosen-item {
    margin: 0 10px 10px 0;
    padding: 5px;
    background: gainsboro;
    border-radius: 10px;
    display: flex;
    align-items: center;
  }
}
.input-terms {
  width: 100%;
  background: #e5e5e7;
  border-radius: 5px;
  height: 36px;
  border: none;
  outline: none;
  font-size: 16px;
  padding: 17px 0 16px 16px;
  border: 1px solid #cdcdce;
  box-sizing: border-box;
}
</style>
